import { useHistory, useRouteMatch, useParams, useLocation } from 'react-router-dom';
import { routeReplacer } from '../../Helpers/String.helper';
import { SKIPPED_ROUTES } from '~/Constants/Routes.constants';
import { useCallback } from 'react';
import { useNavigationContext } from './useNavigationContext';
import { useHasAccess } from '../useHasAccess';

type Params = ReturnType<typeof useParams>;
type LocationState = ReturnType<typeof useLocation>['state'];

export type Navigate = (path: string, params?: Record<string, string | number>) => void;

export const getParameterByName = (name: string, search: string = location.search) => {
  const key = name.replace(/[[\]]/g, '\\$&');
  const regex = new RegExp(`[?&]${key}(=([^&#]*)|&|#|$)`),
    results = regex.exec(search);
  if (!results) return null;
  if (!results[2]) return '';
  return decodeURIComponent(results[2].replace(/\+/g, ' '));
};

export const useNavigation = () => {
  const history = useHistory();
  const match = useRouteMatch();
  const params = useParams();
  const location = useLocation();
  const { hasAccess } = useHasAccess();
  const { isNavigationBlocked, setCallback } = useNavigationContext();

  const removeSearchParams = () => {
    location.search = null as any;
    history.replace(routeReplacer(location.pathname, { ...match.params }));
  };

  const removeSearchParamsHandler = useCallback(() => {
    if (isNavigationBlocked) {
      setCallback(removeSearchParams);
      return () => {};
    }
    return removeSearchParams();
  }, [history, isNavigationBlocked, match, location]);

  const getSearchParams = useCallback(() => {
    const params = location.search.replace('?', '').split('&');
    return params.reduce((map, param) => {
      const keyValPair = param.split('=');
      map[keyValPair[0]] = keyValPair[1];
      return map;
    }, {} as { [key: string]: any });
  }, [location.search]);

  const handleGoBack = useCallback(() => {
    if (isNavigationBlocked) {
      setCallback(() => history.goBack());
      return () => {};
    }
    return history.goBack();
  }, [setCallback, history, isNavigationBlocked]);

  const pushBack = (path: string, params?: Record<string, string | number> | undefined) =>
    hasAccess(path) && history.replace(routeReplacer(path, { ...match.params, ...params }));

  const handlePushBack = useCallback(
    (path: string, params?: Record<string, string | number> | undefined) => {
      if (isNavigationBlocked) {
        setCallback(() => pushBack(path, params));
        return () => {};
      }
      return pushBack(path, params);
    },
    [hasAccess, history, match, isNavigationBlocked, setCallback],
  );

  const navigate = (
    path: string,
    params?: Record<string, string | number> | undefined,
    state?: LocationState,
  ) => {
    if (path !== match.path && hasAccess(path)) {
      const historyHandler = SKIPPED_ROUTES.includes(match.path) ? history.replace : history.push;
      historyHandler(routeReplacer(path, { ...match.params, ...params }), state);
    }
  };

  const handleNavigate = useCallback(
    (path: string, params?: Record<string, string | number> | undefined, state?: LocationState) => {
      if (isNavigationBlocked) {
        setCallback(() => navigate(path, params, state));
        return () => {};
      }
      return navigate(path, params, state);
    },
    [setCallback, isNavigationBlocked, history, match, hasAccess],
  );

  return {
    history,
    goBack: handleGoBack,
    pushBack: handlePushBack, // History overwrite, so we are pushing back onto the stack :D
    navigate: handleNavigate,
    removeSearchParams: removeSearchParamsHandler,
    dangerouslyGoBack: history.goBack,
    dangerouslyPushBack: pushBack,
    dangerouslyNavigate: navigate,
    dangerouslyRemoveSearchParams: removeSearchParams,
    getParams: <T extends object>() => params as T & Params,
    getSearchParams,
    getParameterByName,
  };
};
